import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import heroImg from '../images/hero.jpg';
import about1Img from '../images/about1.jpg';
import about2Img from '../images/about2.jpg';
import about3Img from '../images/about3.jpg';
import event1Img from '../images/event1.jpg';
import culturalDayImg from '../images/cultural_day.jpg';
import outdoorImg from '../images/outdoor_learning.jpg';
import nurseryImg from '../images/nursery_room.jpg';
import pix19Img from '../images/pix19.jpg';
import pix14Img from '../images/pix14.jpg';
import { useSiteData } from '../components/SiteData';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const TOUR_STOPS = [
  {
    id: 'classrooms',
    label: 'Interactive Classrooms',
    image: about1Img,
    description:
      'Vibrant, modern classrooms designed to spark curiosity with flexible layouts, digital tools, and hands-on learning stations.',
  },
  {
    id: 'library',
    label: 'Well-Stocked Library',
    image: about2Img,
    description:
      'A treasure trove of over 5,000 books — from storybooks to academic references — nurturing a lifelong love for reading.',
  },
  {
    id: 'playground',
    label: 'Safe Playground',
    image: about3Img,
    description:
      'Purpose-built play areas where children develop physical fitness, social skills, and teamwork in a safe environment.',
  },
  {
    id: 'events',
    label: 'School Events',
    image: event1Img,
    description:
      'From annual competitions to cultural celebrations, our events build community spirit and lasting memories.',
  },
];

const PILLARS = [
  {
    image: about1Img,
    title: 'Academic Rigor',
    text: 'A challenging curriculum designed to foster critical thinking, creativity, and a lifelong love of learning.',
    /* Custom SVG — not a generic icon */
    svgPath: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  },
  {
    image: culturalDayImg,
    title: 'Athletic Prowess',
    text: 'Physical education programs that build strength, character, and teamwork through competitive and recreational sports.',
    svgPath:
      'M12 2a5 5 0 1 0 0 10A5 5 0 0 0 12 2zM8 14c-4 0-6 2-6 3v1h16v-1c0-1-2-3-6-3h-4z',
  },
  {
    image: event1Img,
    title: 'Creative Arts',
    text: 'Comprehensive programs in visual and performing arts that encourage students to express themselves and discover hidden talents.',
    svgPath:
      'M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z',
  },
];

const VALUES = [
  {
    num: '01',
    label: 'Integrity',
    text: 'Building trust and responsibility in every action.',
  },
  {
    num: '02',
    label: 'Passion',
    text: 'Fuelling the drive to learn and grow every single day.',
  },
  {
    num: '03',
    label: 'Hard Work',
    text: 'Instilling dedication and persistence in all that we do.',
  },
  {
    num: '04',
    label: 'Team Spirit',
    text: 'Encouraging collaboration, empathy, and mutual support.',
  },
  {
    num: '05',
    label: 'Excellence',
    text: 'Striving for quality and continuous improvement always.',
  },
];

/* Minimal reveal hook */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, visible];
}

function Reveal({ children, className = '', delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`home-reveal ${visible ? 'home-reveal--in' : ''} ${className}`}
      style={{ '--reveal-delay': `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function Home() {
  const initialData = useSiteData();
  const [tourIndex, setTourIndex] = useState(0);
  const [newsItems, setNewsItems] = useState(initialData.posts?.slice(0, 3) || []);
  const [galleryItems, setGalleryItems] = useState(initialData.gallery?.slice(0, 6) || []);

  /* Auto-advance tour */
  useEffect(() => {
    const id = setInterval(() => setTourIndex((i) => (i + 1) % TOUR_STOPS.length), 5500);
    return () => clearInterval(id);
  }, []);

  /* Fetch preview data */
  useEffect(() => {
    fetch(`${API_BASE}/posts?limit=3`)
      .then((r) => r.json())
      .then((d) => setNewsItems(d.posts || []))
      .catch(() => {});
    fetch(`${API_BASE}/gallery?limit=6`)
      .then((r) => r.json())
      .then((d) => setGalleryItems(d.gallery || []))
      .catch(() => {});
  }, []);

  const prevStop = useCallback(
    () => setTourIndex((i) => (i - 1 + TOUR_STOPS.length) % TOUR_STOPS.length),
    []
  );
  const nextStop = useCallback(
    () => setTourIndex((i) => (i + 1) % TOUR_STOPS.length),
    []
  );
  const current = TOUR_STOPS[tourIndex];

  /* Fallback gallery items when API not populated */
  const previewPhotos =
    galleryItems.length > 0
      ? galleryItems.slice(0, 6)
      : [pix19Img, pix14Img, about1Img, about2Img, nurseryImg, outdoorImg].map(
          (src, i) => ({ id: i, image_url: src, caption: null })
        );

  return (
    <main className="home-page">

      {/* ═══════════════════════════════════════════
          HERO — full-viewport, left-aligned copy
          ═══════════════════════════════════════════ */}
      <section className="home-hero" aria-label="Welcome to Twinkle Toes Schools">
        <div className="home-hero-photo" style={{ backgroundImage: `url(${heroImg})` }} aria-hidden="true" />
        <div className="home-hero-veil" aria-hidden="true" />
        <div className="home-hero-content">
          <p className="home-hero-kicker">Orozo, Abuja — Since 2016</p>
          <h1 className="home-hero-h1">
            Sure Steps to<br />
            <em>Greatness</em>
          </h1>
          <p className="home-hero-sub">
            Crèche, nursery and primary education in Cajaah Estate, Orozo, Abuja,
            with academic excellence and personal growth at every step.
          </p>
          <div className="home-hero-ctas">
            <Link to="/admissions" className="home-btn home-btn--gold">
              Apply for Admission
            </Link>
            <Link to="/about" className="home-btn home-btn--ghost">
              Explore Our School
            </Link>
          </div>
        </div>

        <div className="home-hero-scroll" aria-hidden="true"><span /></div>
      </section>

      {/* ═══════════════════════════════════════════
          STATS ROW — Gold brand accent band
          ═══════════════════════════════════════════ */}
      <section className="home-stats" aria-label="School statistics at a glance">
        <div className="home-stat">
          <strong>15+</strong>
          <span>Years of Excellence</span>
        </div>
        <div className="home-stat">
          <strong>500+</strong>
          <span>Alumni</span>
        </div>
        <div className="home-stat">
          <strong>3</strong>
          <span>Curricula</span>
        </div>
        <div className="home-stat">
          <strong>1:10</strong>
          <span>Student Ratio</span>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          WELCOME — 2-col, photo + text
          ═══════════════════════════════════════════ */}
      <section className="home-intro">
        <Reveal className="home-intro-photo-col">
          <div className="home-intro-photo-frame">
            <img src={about1Img} alt="Students engaged in classroom activity at Twinkle Toes Schools" />
            <div className="home-intro-photo-badge">
              <strong>100%</strong>
              <span>Dedicated Staff</span>
            </div>
          </div>
        </Reveal>
        <Reveal className="home-intro-text-col" delay={130}>
          <h2 className="home-section-h2">Where Every Child<br />Finds Their Greatness</h2>
          <p>
            At Twinkle Toes Schools, we believe every child arrives with extraordinary
            potential. Our modern curriculum combines the rigour of classical education
            with the creative freedom young minds need to truly flourish.
          </p>
          <p>
            Set in Orozo, Abuja, our school is a place where curiosity is celebrated,
            character is built, and every student is known by name.
          </p>
          <ul className="home-checklist">
            {[
              'Experienced & Dedicated Faculty',
              'State-of-the-art Facilities',
              'Holistic Extracurricular Programs',
            ].map((item) => (
              <li key={item}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" fill="var(--primary)" opacity=".1"/>
                  <path d="M8 12l3 3 5-5" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="home-intro-actions">
            <Link to="/about" className="home-btn home-btn--maroon">Learn About Us</Link>
          </div>
        </Reveal>
      </section>

      {/* ═══════════════════════════════════════════
          VIRTUAL TOUR — maroon band
          ═══════════════════════════════════════════ */}
      <section className="home-tour" aria-label="Virtual school tour">
        <div className="home-tour-inner">
          <Reveal className="home-tour-header">
            <h2 className="home-section-h2 home-section-h2--light">Virtual School Tour</h2>
            <p className="home-tour-sub">
              Step inside and explore what makes Twinkle Toes a place where children thrive.
            </p>
          </Reveal>
          <div className="home-tour-viewer">
            {/* Photo */}
            <div className="home-tour-photo-wrap">
              <img
                key={current.id}
                src={current.image}
                alt={current.label}
                className="home-tour-img"
              />
              <div className="home-tour-img-label" aria-hidden="true">{current.label}</div>
            </div>
            {/* Panel */}
            <div className="home-tour-panel">
              <nav className="home-tour-nav" aria-label="Tour stops">
                {TOUR_STOPS.map((stop, i) => (
                  <button
                    key={stop.id}
                    className={`home-tour-tab${i === tourIndex ? ' home-tour-tab--active' : ''}`}
                    onClick={() => setTourIndex(i)}
                    aria-current={i === tourIndex ? 'true' : undefined}
                  >
                    <span className="home-tour-tab-num" aria-hidden="true">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {stop.label}
                  </button>
                ))}
              </nav>
              <div className="home-tour-description" aria-live="polite">
                <h3>{current.label}</h3>
                <p>{current.description}</p>
              </div>
              <div className="home-tour-controls">
                <button onClick={prevStop} aria-label="Previous stop" className="home-tour-arrow">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <span className="home-tour-counter" aria-live="polite">
                  {tourIndex + 1} / {TOUR_STOPS.length}
                </span>
                <button onClick={nextStop} aria-label="Next stop" className="home-tour-arrow">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          PILLARS — alternating full-bleed rows, NOT identical card grid
          ═══════════════════════════════════════════ */}
      <section className="home-pillars" aria-label="Pillars of excellence">
        <Reveal className="home-pillars-header">
          <h2 className="home-section-h2">What We Stand For</h2>
          <p className="home-pillars-sub">
            Three pillars that shape every learning experience at Twinkle Toes.
          </p>
        </Reveal>
        {PILLARS.map((pillar, i) => (
          <Reveal
            key={pillar.title}
            className={`home-pillar-row${i % 2 !== 0 ? ' home-pillar-row--flip' : ''}`}
            delay={i * 80}
          >
            <div className="home-pillar-photo">
              <img src={pillar.image} alt={pillar.title} loading="lazy" />
            </div>
            <div className="home-pillar-text">
              <div className="home-pillar-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d={pillar.svgPath} />
                </svg>
              </div>
              <h3>{pillar.title}</h3>
              <p>{pillar.text}</p>
            </div>
          </Reveal>
        ))}
      </section>

      {/* ═══════════════════════════════════════════
          MISSION + VISION — side-by-side maroon cards
          ═══════════════════════════════════════════ */}
      <Reveal className="home-mv-band">
        <div className="home-mv-inner">
          <div className="home-mv-card">
            <div className="home-mv-label">Mission</div>
            <h2>Our Mission</h2>
            <p>
              To develop future leaders by providing quality learning opportunities
              that shape confident, creative young minds ready to meet the challenges
              of a complex world.
            </p>
          </div>
          <div className="home-mv-divider" aria-hidden="true" />
          <div className="home-mv-card">
            <div className="home-mv-label">Vision</div>
            <h2>Our Vision</h2>
            <p>
              To be an innovative, world-class provider of quality education — inspiring
              creativity, leadership, and excellence in young Nigerians who will shape
              tomorrow's Africa.
            </p>
          </div>
        </div>
      </Reveal>

      {/* ═══════════════════════════════════════════
          CORE VALUES — horizontal numbered rows (no emoji icons)
          ═══════════════════════════════════════════ */}
      <section className="home-values">
        <Reveal className="home-values-header">
          <h2 className="home-section-h2">Our Core Values</h2>
        </Reveal>
        <div className="home-values-grid">
          {VALUES.map((v, i) => (
            <Reveal key={v.label} className="home-value-item" delay={i * 70}>
              <span className="home-value-num" aria-hidden="true">{v.num}</span>
              <div className="home-value-body">
                <h3>{v.label}</h3>
                <p>{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          GALLERY PREVIEW — bento-style
          ═══════════════════════════════════════════ */}
      <section className="home-gallery-preview">
        <Reveal className="home-gallery-header">
          <div>
            <h2 className="home-section-h2">School Moments</h2>
            <p className="home-gallery-sub">A glimpse into our vibrant school life.</p>
          </div>
          <Link to="/gallery" className="home-btn home-btn--maroon">View Full Gallery</Link>
        </Reveal>
        <div className="home-gallery-bento">
          {previewPhotos.map((item, idx) => (
            <div
              key={item.id ?? idx}
              className={`home-gallery-item${idx === 0 ? ' home-gallery-item--feature' : idx === 3 ? ' home-gallery-item--tall' : ''}`}
            >
              <img
                src={item.image_url}
                alt={item.caption || `School moment ${idx + 1}`}
                loading="lazy"
              />
              {item.caption && (
                <div className="home-gallery-caption">{item.caption}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          LATEST NEWS — shown only if there are posts
          ═══════════════════════════════════════════ */}
      {newsItems.length > 0 && (
        <section className="home-news-preview">
          <Reveal className="home-news-header">
            <div>
              <h2 className="home-section-h2">Latest News &amp; Events</h2>
              <p className="home-news-sub">Stay up to date with what's happening at Twinkle Toes.</p>
            </div>
            <Link to="/news" className="home-btn home-btn--maroon">All News</Link>
          </Reveal>
          <div className="home-news-grid">
            {newsItems.map((post, i) => (
              <Reveal key={post.id} delay={i * 90}>
                <Link to={`/news/${post.id}`} className="home-news-card">
                  {post.image_url && (
                    <div className="home-news-img">
                      <img src={post.image_url} alt={post.title} loading="lazy" />
                    </div>
                  )}
                  <div className="home-news-body">
                    {post.category && (
                      <span className="home-news-cat">{post.category}</span>
                    )}
                    <h3>{post.title}</h3>
                    {post.excerpt && <p>{post.excerpt}</p>}
                    <span className="home-news-read" aria-hidden="true">
                      Read article
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════
          CTA BAND — gold bg
          ═══════════════════════════════════════════ */}
      <section className="home-cta-band" aria-label="Admissions call to action">
        <Reveal className="home-cta-inner">
          <div className="home-cta-text">
            <h2>Ready to Join the<br />Twinkle Toes Family?</h2>
            <p>Give your child the very best start. Enquire about admissions today.</p>
          </div>
          <div className="home-cta-actions">
            <Link to="/admissions" className="home-btn home-btn--maroon">Apply for Admission</Link>
            <Link to="/contact" className="home-btn home-btn--outline-dark">Contact Us</Link>
          </div>
        </Reveal>
      </section>

    </main>
  );
}

export default Home;
